import { centsToFormattedDollars, formatDollars } from '@fruitful/fruitils';
import {
    type AccountSummary,
    type ApexAccountRealtimeBalanceData,
    ApexDerivedSubState,
    type ContributionSummary,
    type ImAccount,
    Provider,
    ReadableApexAccountRegistrationType,
    SupportedApexAccountRegistrationType,
} from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import * as React from 'react';
import { useCallback } from 'react';
import {
    BodyMediumRegular,
    Notice,
    SmoothieScrollView,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../../common/components/GroveButtons';
import { adminUserAtom } from '../../../entityAtoms';

const getReadableState = (state: ApexDerivedSubState) => {
    switch (state) {
        case ApexDerivedSubState.OPEN_ACTIVE:
            return 'Open';
        case ApexDerivedSubState.OPEN_RESTRICTED:
            return 'Open with restrictions';
        case ApexDerivedSubState.PENDING_INVESTIGATION_MANUAL_REVIEW:
        case ApexDerivedSubState.PENDING_INVESTIGATION_RUN:
        case ApexDerivedSubState.PENDING_PARTNER_ACCEPT:
            return 'Pending';
        case ApexDerivedSubState.OPEN_IN_CLOSING:
            return 'In closing';
        case ApexDerivedSubState.CLOSED:
            return 'Closed';
    }
};

const getReadableAccountType = (
    registrationType: SupportedApexAccountRegistrationType,
) => {
    const type: string | undefined =
        ReadableApexAccountRegistrationType[registrationType];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Pending account have unspecified type
    if (type == null || type.length === 0) {
        return 'Unspecified';
    }
    return type;
};

export const InvestmentAccountTable = ({
    account,
    onApexDeposit,
    onApexWithdrawal,
}: {
    account: AccountSummary | null;
    onApexDeposit?: (account: AccountSummary) => void;
    onApexWithdrawal?: (account: AccountSummary) => void;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canUseImOpsFunctionality =
        adminUser.authorization.imOpsFunctionality.canUse;
    const isApexAccount = (
        account,
    ): account is AccountSummary & { apexAccount: ImAccount } =>
        'apexAccountId' in account;

    if (isApexAccount(account)) {
        const { apexAccount } = account;
        const {
            nickname,
            accountNumber,
            apexAccountId,
            backdoorRothRole,
            derivedSubState,
            registrationType,
        } = apexAccount;

        const accountTypeFn = useCallback(getReadableAccountType, [account]);
        const readableStateFn = useCallback(getReadableState, [account]);

        const accountType = accountTypeFn(registrationType);
        const state = readableStateFn(derivedSubState);

        return (
            <SmoothieScrollView
                containerStyle={{
                    minWidth: '60vw',
                    maxHeight: '70vh',
                }}
            >
                <YStack gap={8}>
                    <YStack gap={8} paddingVertical={16}>
                        <BodyMediumRegular>Apex account info</BodyMediumRegular>
                        <DataTable
                            value={[
                                {
                                    nickname,
                                    apexAccountId,
                                    accountNumber,
                                    state,
                                    accountType,
                                    backdoorRothRole,
                                },
                            ]}
                        >
                            <Column header="Name" field="nickname" />
                            <Column header="Type" field="accountType" />
                            <Column header="State" field="state" />
                            <Column header="Account id" field="apexAccountId" />
                            <Column
                                header="Account number"
                                field="accountNumber"
                            />
                            <Column
                                header="Backdoor Roth"
                                field="backdoorRothRole"
                                body={(value: {
                                    backdoorRothRole: string | null;
                                }) => {
                                    return value.backdoorRothRole ?? 'N/A';
                                }}
                            />
                        </DataTable>
                    </YStack>
                    <YStack gap={8} paddingVertical={16}>
                        <BodyMediumRegular>Balances</BodyMediumRegular>
                        <DataTable<ApexAccountRealtimeBalanceData[]>
                            value={[apexAccount.realtimeBalanceData]}
                        >
                            <Column
                                header="Total Equity"
                                field="accountEquityCents"
                                body={(
                                    value: ApexAccountRealtimeBalanceData,
                                ) => {
                                    return centsToFormattedDollars(
                                        value.accountEquityCents,
                                    );
                                }}
                            />
                            <Column
                                header="Position Value"
                                field="positionMarketValueCents"
                                body={(value: ApexAccountRealtimeBalanceData) =>
                                    centsToFormattedDollars(
                                        value.positionMarketValueCents,
                                    )
                                }
                            />
                            <Column
                                header="Cash"
                                field="cashCents"
                                body={(value: ApexAccountRealtimeBalanceData) =>
                                    centsToFormattedDollars(value.cashCents)
                                }
                            />
                        </DataTable>
                    </YStack>
                    {apexAccount.isRetirementAccount &&
                        apexAccount.registrationType ===
                            SupportedApexAccountRegistrationType.RegistrationIraSep && (
                            <Notice
                                variant="warning"
                                title="SEP IRA Contributions"
                                description="Employer SEP IRA contributions are not tracked by Apex at this time"
                            />
                        )}
                    {apexAccount.isRetirementAccount &&
                        apexAccount.registrationType !==
                            SupportedApexAccountRegistrationType.RegistrationIraSep &&
                        apexAccount.contributionSummaries.length > 0 && (
                            <YStack gap={8} paddingVertical={16}>
                                <BodyMediumRegular>
                                    Retirement Contributions
                                </BodyMediumRegular>
                                <DataTable
                                    value={apexAccount.contributionSummaries}
                                >
                                    <Column header="Tax Year" field="taxYear" />
                                    <Column
                                        header="Contributions"
                                        field="regularAmount.value"
                                        body={(value: ContributionSummary) =>
                                            formatDollars(
                                                parseFloat(
                                                    value.regularAmount
                                                        ?.value ?? '0',
                                                ),
                                            )
                                        }
                                    />
                                    <Column
                                        header="Remaining"
                                        field="remainingContributionAllowed.value"
                                        body={(value: ContributionSummary) => {
                                            return value
                                                .remainingContributionAllowed
                                                ?.value != null
                                                ? formatDollars(
                                                      parseFloat(
                                                          value
                                                              .remainingContributionAllowed
                                                              .value,
                                                      ),
                                                  )
                                                : '';
                                        }}
                                    />
                                </DataTable>
                            </YStack>
                        )}
                    {canUseImOpsFunctionality &&
                        account.provider === Provider.APEX && (
                            <YStack
                                gap={12}
                                paddingVertical={16}
                                borderTopWidth={1}
                                borderTopColor="$gray200"
                                marginTop={8}
                            >
                                <BodyMediumRegular color="$gray1000">
                                    IM Admin Actions
                                </BodyMediumRegular>
                                <XStack gap={12}>
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        disabled={onApexDeposit == null}
                                        onPress={() => {
                                            onApexDeposit?.(account);
                                        }}
                                    >
                                        Initiate Deposit
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        disabled={
                                            onApexWithdrawal == null ||
                                            (account.currentBalanceCents ??
                                                0) <= 0
                                        }
                                        onPress={() => {
                                            onApexWithdrawal?.(account);
                                        }}
                                    >
                                        Initiate Withdrawal
                                    </Button>
                                </XStack>
                            </YStack>
                        )}
                </YStack>
            </SmoothieScrollView>
        );
    } else {
        return null;
    }
};
